import { FILETIME_CORRECTION, _SIGNATURE_MARKER } from "../constants";
import { DocumentData, DocumentFields, CreateBufferMethods } from "../types";
import { createBuffer } from "./create-buffer";
import { makeCryptoControlBlock } from "./make-crypto-control-block";

function getRandomInt(min: number, max: number): number {
  const byteArray = new Uint8Array(1);

  window.crypto.getRandomValues(byteArray);

  const range = max - min + 1;
  const max_range = 1000;

  if (byteArray[0] >= Math.floor(max_range / range) * range) {
    return getRandomInt(min, max);
  }

  return min + (byteArray[0] % range);
}

export const preparePattern = () => {
  let pattern = "";
  const symbolString =
    "AaBbCcDdEeFfGgHhIiJjKkLlMmNnOoPpQqRrSsTtUuVvWwXxYyZx1234567890[]{}().,;:'\"!@#$%^&*";

  const randomLength = getRandomInt(500, 1000);

  for (let i = 0; i < randomLength; i++) {
    pattern += symbolString.charAt(
      Math.floor((getRandomInt(10, 100) / 100) * symbolString.length)
    );
  }

  return pattern;
};

export const encodePattern = (pattern: string) =>
  createBuffer().encodeToBase64AndPack(pattern).toBase64String();

const filetime = (millis: number) =>
  createBuffer()
    .decodeFromHex((FILETIME_CORRECTION + Number(millis) * 10000).toString(16))
    .inverse();

const wrapDetachedSignature = (
  certid: CreateBufferMethods,
  signature: CreateBufferMethods
): string => {
  let nSignature: CreateBufferMethods | string = signature;

  nSignature = createBuffer()
    .addInt(_SIGNATURE_MARKER)
    .merge(filetime(new Date().getTime()))
    .addInt(0x0000801e)
    .addInt(1)
    .addInt(36 + certid.nBytes() + nSignature.nBytes())
    .addInt(certid.nBytes())
    .addInt(nSignature.nBytes())
    .merge(certid)
    .merge(nSignature);
  nSignature = createBuffer()
    .addInt(nSignature.crc())
    .merge(nSignature)
    .toBase64String();

  return nSignature + createBuffer().addInt(nSignature.length).toBase64String();
};

const wrapPKCS7 = (certificateId: string, signature: CreateBufferMethods) =>
  wrapDetachedSignature(
    createBuffer().decodeFromHex(certificateId).inverse(),
    signature
  );

/**
 * Функция для обертки подписи в формате PKS7
 */
export const buildSignature = (
  certificateId: string,
  signature: string,
  result: (signature: string) => void
) =>
  result(wrapPKCS7(certificateId, createBuffer().decodeFromBase64(signature)));

/**
 * Функция для конвертации UTF-8 строки в base64
 *
 * Пример: base64Encode("hello world") -> aGVsbG8gd29ybGQ=
 * Пример: base64Encode("test") -> dGVzdA==
 */
const base64Encode = (string: string) =>
  btoa(
    encodeURIComponent(string).replace(
      /%([0-9A-F]{2})/g,
      function toSolidBytes(_, p1) {
        return String.fromCharCode(parseInt(p1, 16));
      }
    )
  );

/**
 * Преобразование полей ККБ в base64 UTF-8 или UTF-16
 *
 * Пример: prepareCryptoControlBlock("hello world") -> aGVsbG8gd29ybGQ=
 * Пример: prepareCryptoControlBlock("{data:[{suffix:"test", preffix:"test", value:"test"}], foter:"test", header:"test"}") -> ZEFCbEFITUFkQUIwQUdVQWN3QjBBSFFBWlFCekFIUUFkQUJsQUhNQWRBQjBBR1VBY3dCMEFBPT0=
 */
export const prepareCryptoControlBlock = (
  document: DocumentData | DocumentFields[] | string
): string => {
  if (typeof document === "string") {
    return base64Encode(document);
  }

  return createBuffer()
    .encodeToBase64AndPack(makeCryptoControlBlock(document))
    .toBase64String();
};
